import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2, Square, SplitSquareHorizontal, SplitSquareVertical, ChevronDown, ChevronUp } from 'lucide-react';
import { getViewportCropUrl } from '../utils/cropUtils';
import { 
  getPrimaryOriginalUrl, 
  getSecondaryOriginalUrl 
} from '@/utils/canvasCropping';

interface EditorSidebarProps {
  asset: any;
  editorState: any;
  annotationState: any;
  maskState: any;
  imageRef: React.RefObject<HTMLImageElement>;
  secondaryImageRef: React.RefObject<HTMLImageElement>;
  onAspectRatioChange: (ratio: string) => void;
  onComparisonModeChange: (mode: string) => void;
  onSave: () => void;
  onCancel: () => void;
  getOffsetCoordinates?: (isPrimary?: boolean) => { x: number; y: number };
  pageData?: any;
  pageDataLoaded?: boolean;
}

export const EditorSidebar: React.FC<EditorSidebarProps> = ({
  asset,
  editorState,
  annotationState,
  maskState,
  imageRef,
  secondaryImageRef,
  onAspectRatioChange,
  onComparisonModeChange,
  onSave,
  onCancel,
  getOffsetCoordinates,
  pageData,
  pageDataLoaded
}) => {
  const [isDebugExpanded, setIsDebugExpanded] = useState(false);
  // Helper function to get effective primary original URL (considers version switching)
  const getEffectivePrimaryOriginalUrl = () => {
    // Prioritize switched version data if available
    if (editorState.primaryImageData?.imageUrl) {
      return editorState.primaryImageData.imageUrl;
    }
    // Fall back to asset's original URL
    return getPrimaryOriginalUrl(asset);
  };

  // Helper function to get effective secondary original URL (considers version switching)
  const getEffectiveSecondaryOriginalUrl = () => {
    // Prioritize switched version data if available
    if (editorState.secondaryImageData?.imageUrl) {
      return editorState.secondaryImageData.imageUrl;
    }
    // Fall back to asset's original URL
    return getSecondaryOriginalUrl(asset);
  };
  // Helper function to check if URL is Cloudinary
  const isCloudinaryUrl = (url: string): boolean => {
    return url.includes('cloudinary.com') || url.includes('res.cloudinary.com');
  };

  // Cache for markup image dimensions to avoid repeated loading
  const [markupImageDimensions, setMarkupImageDimensions] = React.useState<Map<string, {width: number, height: number}>>(new Map());

  // Helper function to get actual markup image dimensions
  const getMarkupImageDimensions = async (imageUrl: string): Promise<{width: number, height: number}> => {
    // Check cache first
    const cached = markupImageDimensions.get(imageUrl);
    if (cached) {
      return cached;
    }

    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        const dimensions = {
          width: img.naturalWidth,
          height: img.naturalHeight
        };
        
        // Cache the dimensions
        setMarkupImageDimensions(prev => new Map(prev).set(imageUrl, dimensions));
        
        
        resolve(dimensions);
      };
      
      img.onerror = () => {
        // Fallback to reasonable defaults
        resolve({ width: 1200, height: 900 });
      };
      
      img.src = imageUrl;
    });
  };

  // Helper function to generate real-time markup URL with current transforms
  const getRealTimeMarkupUrl = (markupUrl: string, isPrimary: boolean = true) => {
    if (!markupUrl || !getOffsetCoordinates) return markupUrl;
    
    // Only apply Cloudinary transforms to Cloudinary URLs
    if (!isCloudinaryUrl(markupUrl)) {
      return markupUrl; // Return original Supabase URL unchanged
    }
    
    // Get current zoom/pan coordinates
    const coordinates = isPrimary ? editorState.zoomCoordinates : editorState.secondaryZoomCoordinates;
    const offset = getOffsetCoordinates(isPrimary);
    
    // Generate crop URL with actual image dimensions
    return generateMarkupCropUrlAsync(markupUrl, coordinates, offset);
  };

  // Async helper function that loads actual image dimensions and generates crop URL
  const generateMarkupCropUrlAsync = (
    markupUrl: string, 
    coordinates: any, 
    offset: { x: number, y: number }
  ): string => {
    // Check if we have cached dimensions
    const cached = markupImageDimensions.get(markupUrl);
    if (cached) {
      return generateMarkupCropUrlWithDimensions(markupUrl, coordinates, offset, cached);
    }
    
    // If no cached dimensions, trigger async loading and return original URL for now
    getMarkupImageDimensions(markupUrl).then(dimensions => {
      // This will trigger a re-render when dimensions are loaded
      // The next call will use the cached dimensions
    }).catch(error => {
    });
    
    // Return original URL while dimensions are loading
    return markupUrl;
  };

  // Helper function to generate Cloudinary crop URL with known image dimensions
  const generateMarkupCropUrlWithDimensions = (
    markupUrl: string, 
    coordinates: any, 
    offset: { x: number, y: number },
    imageDimensions: { width: number, height: number }
  ): string => {
    const { scale, x, y, viewportWidth, viewportHeight } = coordinates;
    const { width: actualWidth, height: actualHeight } = imageDimensions;
    
    
    // SIMPLIFIED APPROACH: Instead of trying to convert coordinate systems,
    // let's think about this differently. The markup image is being displayed
    // with pan/zoom just like the original. We need to calculate what portion
    // of the markup image corresponds to the current viewport view.
    
    // The key insight: if the markup image has different dimensions than expected,
    // we need to scale our calculations proportionally.
    
    // Assume the markup image is meant to fill the same logical space as the original
    // Calculate the scaling factor between actual markup dimensions and viewport
    const scaleFactorX = actualWidth / viewportWidth;
    const scaleFactorY = actualHeight / viewportHeight;
    
    // Calculate the visible area in markup image coordinates
    // When scale = 1 and pan = (0,0), we see the full viewport
    // When scale > 1, we see a smaller portion (zoomed in)
    // When pan != (0,0), the view is shifted
    
    const visibleWidth = viewportWidth / scale;
    const visibleHeight = viewportHeight / scale;
    
    // Calculate the top-left corner of the visible area in normalized coordinates
    const normalizedX = -x / scale / viewportWidth;
    const normalizedY = -y / scale / viewportHeight;
    
    // Convert to markup image coordinates
    let cropX = normalizedX * actualWidth;
    let cropY = normalizedY * actualHeight;
    let cropW = (visibleWidth / viewportWidth) * actualWidth;
    let cropH = (visibleHeight / viewportHeight) * actualHeight;
    
    // Round to integers
    cropX = Math.round(cropX);
    cropY = Math.round(cropY);
    cropW = Math.round(cropW);
    cropH = Math.round(cropH);
    
    // BOUNDARY VALIDATION: Ensure crop parameters are within image bounds
    cropX = Math.max(0, Math.min(cropX, actualWidth - 1));
    cropY = Math.max(0, Math.min(cropY, actualHeight - 1));
    cropW = Math.max(1, Math.min(cropW, actualWidth - cropX));
    cropH = Math.max(1, Math.min(cropH, actualHeight - cropY));
    
    // If crop area is too small, return original URL
    if (cropW < 10 || cropH < 10) {
      return markupUrl;
    }
    
    
    // Build Cloudinary transformation
    const cropTransform = `c_crop,x_${cropX},y_${cropY},w_${cropW},h_${cropH}`;
    const qualityTransform = 'q_auto,f_auto';
    const transformations = `${cropTransform}/${qualityTransform}`;
    
    // Apply transformation to URL
    if (markupUrl.includes('/image/upload/')) {
      const uploadIndex = markupUrl.indexOf('/image/upload/');
      const afterUpload = markupUrl.substring(uploadIndex + '/image/upload/'.length);
      const versionIndex = afterUpload.indexOf('/v');
      
      if (versionIndex > 0) {
        const beforeVersion = markupUrl.substring(0, uploadIndex + '/image/upload/'.length);
        const fromVersion = afterUpload.substring(versionIndex);
        return `${beforeVersion}${transformations}${fromVersion}`;
      } else {
        return markupUrl.replace('/image/upload/', `/image/upload/${transformations}/`);
      }
    } else if (markupUrl.includes('/upload/')) {
      const uploadIndex = markupUrl.indexOf('/upload/');
      const afterUpload = markupUrl.substring(uploadIndex + '/upload/'.length);
      const versionIndex = afterUpload.indexOf('/v');
      
      if (versionIndex > 0) {
        const beforeVersion = markupUrl.substring(0, uploadIndex + '/upload/'.length);
        const fromVersion = afterUpload.substring(versionIndex);
        return `${beforeVersion}${transformations}${fromVersion}`;
      } else {
        return markupUrl.replace('/upload/', `/upload/${transformations}/`);
      }
    }
    
    return markupUrl;
  };

  // Helper function to get crop parameters display
  const getCropParametersDisplay = (isPrimary: boolean = true) => {
    if (!getOffsetCoordinates) return '';
    
    const offset = getOffsetCoordinates(isPrimary);
    const coordinates = isPrimary ? editorState.zoomCoordinates : editorState.secondaryZoomCoordinates;
    
    return `Scale: ${coordinates.scale.toFixed(2)}, Pan: (${coordinates.x}, ${coordinates.y}), Offset: (${offset.x}, ${offset.y})`;
  };

  return (
    <div className="w-80 bg-gray-50 border-r border-gray-200 flex flex-col p-4 space-y-4 relative">
      {/* Company Version & Slug */}
      <div className="pb-4 border-b border-gray-200">
        <div className="text-sm font-medium text-gray-900 mb-1">
          {asset.slug || 'Unknown Company'}
        </div>
        <div className="text-xs text-gray-600">
          Version: {asset.version || 'N/A'}
        </div>
      </div>
      
      {/* View Mode Controls */}
      <div className="space-y-3">
        {editorState.showComparisonModeControls && (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">View Mode</label>
            <div className="grid grid-cols-3 gap-1">
              <Button
                size="sm"
                variant={editorState.comparisonMode === 'single' ? 'default' : 'outline'}
                onClick={() => editorState.canChangeComparisonMode && onComparisonModeChange('single')}
                className="text-xs px-2"
                title="Single view"
                disabled={!editorState.canChangeComparisonMode}
              >
                <Square className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant={editorState.comparisonMode === 'side_by_side' ? 'default' : 'outline'}
                onClick={() => editorState.canChangeComparisonMode && onComparisonModeChange('side_by_side')}
                className="text-xs px-2"
                title="Side-by-side view"
                disabled={!editorState.canChangeComparisonMode}
              >
                <SplitSquareHorizontal className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant={editorState.comparisonMode === 'stacked' ? 'default' : 'outline'}
                onClick={() => editorState.canChangeComparisonMode && onComparisonModeChange('stacked')}
                className="text-xs px-2"
                title="Stacked view"
                disabled={!editorState.canChangeComparisonMode}
              >
                <SplitSquareVertical className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {editorState.showAspectRatioControls && (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">Aspect Ratio</label>
            <div className="grid grid-cols-3 gap-1">
              <Button
                size="sm"
                variant={editorState.selectedAspectRatio === '1:1' ? 'default' : 'outline'}
                onClick={() => editorState.canChangeAspectRatio && onAspectRatioChange('1:1')}
                className="text-xs px-2"
                disabled={!editorState.canChangeAspectRatio}
              >
                1:1
              </Button>
              <Button
                size="sm"
                variant={editorState.selectedAspectRatio === '16:9' ? 'default' : 'outline'}
                onClick={() => editorState.canChangeAspectRatio && onAspectRatioChange('16:9')}
                className="text-xs px-2"
                disabled={!editorState.canChangeAspectRatio}
              >
                16:9
              </Button>
              <Button
                size="sm"
                variant={editorState.selectedAspectRatio === '4:3' ? 'default' : 'outline'}
                onClick={() => editorState.canChangeAspectRatio && onAspectRatioChange('4:3')}
                className="text-xs px-2"
                disabled={!editorState.canChangeAspectRatio}
              >
                4:3
              </Button>
            </div>
          </div>
        )}

      </div>
      
      {/* Debug Section */}
      <div className="pb-24">
        <div className="bg-white rounded-lg border border-gray-200">
          <button
            onClick={() => setIsDebugExpanded(!isDebugExpanded)}
            className="w-full p-3 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
          >
            <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
              🔧 Debug Output
            </h3>
            {isDebugExpanded ? (
              <ChevronUp className="h-4 w-4 text-gray-500" />
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-500" />
            )}
          </button>
          
          {isDebugExpanded && (
            <div className="p-3 pt-0 h-80 overflow-y-auto border-t border-gray-100">
          
          {/* Page Data Status */}
          <div className="mb-4">
            <h4 className="text-xs font-medium text-gray-700 mb-2">Page Data Status</h4>
            <div className={pageDataLoaded ? 'bg-green-50 rounded p-2 border border-green-200' : 'bg-red-50 rounded p-2 border border-red-200'}>
              <div className="text-xs font-medium mb-1">
                Status: <span className={pageDataLoaded ? 'text-green-700' : 'text-red-700'}>
                  {pageDataLoaded ? '✅ LOADED' : '❌ NOT LOADED'}
                </span>
              </div>
              <div className="text-xs text-gray-600 mb-1">
                Asset Page ID: <span className="font-mono">{asset?.page_id || 'undefined'}</span>
              </div>
              <div className="text-xs text-gray-600 mb-1">
                Quarters Available: <span className="font-mono">{pageData?.quarters?.length || 0}</span>
              </div>
              <div className="text-xs text-gray-600 mb-1">
                Scrapes Available: <span className="font-mono">{pageData?.scrapes?.length || 0}</span>
              </div>
              <div className="text-xs text-gray-600">
                Summary: {pageData?.quarters ? `${pageData.quarters.length} quarters available` : 'No quarters found'}
              </div>
            </div>
          </div>
          
          {/* Currently Displayed URL */}
          <div className="mb-4">
            <h4 className="text-xs font-medium text-gray-700 mb-2">Currently Displayed in Canvas</h4>
            <div className="bg-blue-50 rounded p-2 border border-blue-200">
              <div className="text-xs text-blue-700 mb-1">Canvas Image Source:</div>
              <div className="text-xs text-blue-800 font-mono break-all">
                {imageRef.current?.src ? imageRef.current.src.substring(0, 100) + '...' : 'Image not loaded'}
              </div>
            </div>
          </div>

          {/* Primary Image URLs */}
          <div className="mb-4">
            <h4 className="text-xs font-medium text-gray-700 mb-2">Primary Image URLs</h4>
            
            <div className="space-y-2">
              {/* Original URL */}
              <div className="bg-gray-50 rounded p-2">
                <div className="text-xs text-gray-600 mb-1">Original:</div>
                {(() => {
                  const originalUrl = getEffectivePrimaryOriginalUrl();
                  return originalUrl ? (
                    <a
                      href={originalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:text-blue-800 underline break-all"
                    >
                      {originalUrl.substring(0, 80)}{originalUrl.length > 80 ? '...' : ''}
                    </a>
                  ) : (
                    <span className="text-xs text-gray-400">Not available</span>
                  );
                })()}
              </div>
              
              {/* Cropped URL */}
              <div className="bg-gray-50 rounded p-2">
                <div className="text-xs text-gray-600 mb-1">Cropped:</div>
                {(() => {
                  const originalImageUrl = getEffectivePrimaryOriginalUrl();
                  if (!originalImageUrl || !imageRef.current) {
                    return <span className="text-xs text-gray-400">No image loaded</span>;
                  }
                  
                  const cropUrl = getViewportCropUrl(
                    originalImageUrl, 
                    editorState.zoomCoordinates, 
                    undefined, 
                    imageRef.current
                  );
                  
                  return (
                    <a
                      href={cropUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:text-blue-800 underline break-all"
                    >
                      {cropUrl.substring(0, 80)}{cropUrl.length > 80 ? '...' : ''}
                    </a>
                  );
                })()}
              </div>
              
              {/* Markup URL */}
              <div className="bg-gray-50 rounded p-2">
                <div className="text-xs text-gray-600 mb-1">Markup:</div>
                {(() => {
                  // Only show CLEARED when reset has been requested AND there was originally a markup URL
                  const shouldShowCleared = editorState.primaryResetRequested?.current && asset.primary_markup_url;
                  
                  if (shouldShowCleared) {
                    return <span className="text-xs text-orange-600 font-medium">CLEARED (pending save)</span>;
                  }
                  
                  if (asset.primary_markup_url) {
                    const realTimeMarkupUrl = editorState.primaryImageMode === 'markup' 
                      ? getRealTimeMarkupUrl(asset.primary_markup_url, true)
                      : asset.primary_markup_url;
                    const isCloudinary = isCloudinaryUrl(asset.primary_markup_url);
                    
                    return (
                      <div>
                        <a
                          href={realTimeMarkupUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-purple-600 hover:text-purple-800 underline break-all"
                        >
                          {realTimeMarkupUrl.substring(0, 80)}{realTimeMarkupUrl.length > 80 ? '...' : ''}
                        </a>
                        {!isCloudinary && editorState.primaryImageMode === 'markup' && (
                          <div className="text-xs text-gray-500 mt-1 font-mono">
                            {getCropParametersDisplay(true)}
                          </div>
                        )}
                      </div>
                    );
                  }
                  
                  return <span className="text-xs text-gray-400">Not available</span>;
                })()}
              </div>
            </div>
          </div>
          
          {/* Secondary Image URLs (only show in dual panel modes) */}
          {editorState.comparisonMode !== 'single' && (
            <div className="mb-4">
              <h4 className="text-xs font-medium text-gray-700 mb-2">Secondary Image URLs</h4>
              
              <div className="space-y-2">
                {/* Original URL */}
                <div className="bg-gray-50 rounded p-2">
                  <div className="text-xs text-gray-600 mb-1">Original:</div>
                  {(() => {
                    const originalUrl = getEffectiveSecondaryOriginalUrl();
                    return originalUrl ? (
                      <a
                        href={originalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-green-600 hover:text-green-800 underline break-all"
                      >
                        {originalUrl.substring(0, 80)}{originalUrl.length > 80 ? '...' : ''}
                      </a>
                    ) : (
                      <span className="text-xs text-gray-400">Not available</span>
                    );
                  })()}
                </div>
                
                {/* Cropped URL */}
                <div className="bg-gray-50 rounded p-2">
                  <div className="text-xs text-gray-600 mb-1">Cropped:</div>
                  {(() => {
                    const secondaryImageUrl = getEffectiveSecondaryOriginalUrl();
                    if (!secondaryImageUrl || !secondaryImageRef.current) {
                      return <span className="text-xs text-gray-400">No secondary image loaded</span>;
                    }
                    
                    // Debug logging for secondary crop URL generation
                    
                    const cropUrl = getViewportCropUrl(
                      secondaryImageUrl, 
                      editorState.secondaryZoomCoordinates, 
                      undefined, 
                      secondaryImageRef.current
                    );
                    
                    
                    return (
                      <a
                        href={cropUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-green-600 hover:text-green-800 underline break-all"
                      >
                        {cropUrl.substring(0, 80)}{cropUrl.length > 80 ? '...' : ''}
                      </a>
                    );
                  })()}
                </div>
                
                {/* Markup URL */}
                <div className="bg-gray-50 rounded p-2">
                  <div className="text-xs text-gray-600 mb-1">Markup:</div>
                  {(() => {
                    // Only show CLEARED when reset has been requested AND there was originally a markup URL
                    const shouldShowCleared = editorState.secondaryResetRequested?.current && asset.secondary_markup_url;
                    
                    if (shouldShowCleared) {
                      return <span className="text-xs text-orange-600 font-medium">CLEARED (pending save)</span>;
                    }
                    
                    if (asset.secondary_markup_url) {
                      const realTimeMarkupUrl = editorState.secondaryImageMode === 'markup' 
                        ? getRealTimeMarkupUrl(asset.secondary_markup_url, false)
                        : asset.secondary_markup_url;
                      const isCloudinary = isCloudinaryUrl(asset.secondary_markup_url);
                      
                      return (
                        <div>
                          <a
                            href={realTimeMarkupUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-purple-600 hover:text-purple-800 underline break-all"
                          >
                            {realTimeMarkupUrl.substring(0, 80)}{realTimeMarkupUrl.length > 80 ? '...' : ''}
                          </a>
                          {!isCloudinary && editorState.secondaryImageMode === 'markup' && (
                            <div className="text-xs text-gray-500 mt-1 font-mono">
                              {getCropParametersDisplay(false)}
                            </div>
                          )}
                        </div>
                      );
                    }
                    
                    return <span className="text-xs text-gray-400">Not available</span>;
                  })()}
                </div>
              </div>
            </div>
          )}
          
          {/* Four Coordinate Sets */}
          <div className="mb-4">
            <h4 className="text-xs font-medium text-gray-700 mb-2">Coordinate Sets (All 4)</h4>
            <div className="space-y-2">
              
              {/* Primary Crop Coordinates */}
              <div className="bg-blue-50 rounded p-2 border border-blue-200">
                <div className="text-xs font-medium text-blue-700 mb-1">Primary Crop:</div>
                <div className="text-xs text-blue-800 font-mono">
                  Scale: {editorState.primaryCropCoordinates.scale.toFixed(2)}, 
                  Pan: ({editorState.primaryCropCoordinates.x}, {editorState.primaryCropCoordinates.y}),
                  Viewport: {editorState.primaryCropCoordinates.viewportWidth}x{editorState.primaryCropCoordinates.viewportHeight}
                </div>
              </div>

              {/* Primary Markup Coordinates */}
              <div className="bg-purple-50 rounded p-2 border border-purple-200">
                <div className="text-xs font-medium text-purple-700 mb-1">Primary Markup:</div>
                <div className="text-xs text-purple-800 font-mono">
                  Scale: {editorState.primaryMarkupCoordinates.scale.toFixed(2)}, 
                  Pan: ({editorState.primaryMarkupCoordinates.x}, {editorState.primaryMarkupCoordinates.y}),
                  Viewport: {editorState.primaryMarkupCoordinates.viewportWidth}x{editorState.primaryMarkupCoordinates.viewportHeight}
                </div>
              </div>

              {/* Secondary Crop Coordinates */}
              <div className="bg-green-50 rounded p-2 border border-green-200">
                <div className="text-xs font-medium text-green-700 mb-1">Secondary Crop:</div>
                <div className="text-xs text-green-800 font-mono">
                  Scale: {editorState.secondaryCropModeCoordinates.scale.toFixed(2)}, 
                  Pan: ({editorState.secondaryCropModeCoordinates.x}, {editorState.secondaryCropModeCoordinates.y}),
                  Viewport: {editorState.secondaryCropModeCoordinates.viewportWidth}x{editorState.secondaryCropModeCoordinates.viewportHeight}
                </div>
              </div>

              {/* Secondary Markup Coordinates */}
              <div className="bg-orange-50 rounded p-2 border border-orange-200">
                <div className="text-xs font-medium text-orange-700 mb-1">Secondary Markup:</div>
                <div className="text-xs text-orange-800 font-mono">
                  Scale: {editorState.secondaryMarkupCoordinates.scale.toFixed(2)}, 
                  Pan: ({editorState.secondaryMarkupCoordinates.x}, {editorState.secondaryMarkupCoordinates.y}),
                  Viewport: {editorState.secondaryMarkupCoordinates.viewportWidth}x{editorState.secondaryMarkupCoordinates.viewportHeight}
                </div>
              </div>

              {/* Currently Active Coordinates */}
              <div className="bg-gray-100 rounded p-2 border border-gray-300">
                <div className="text-xs font-medium text-gray-700 mb-1">Currently Active:</div>
                <div className="text-xs text-gray-800 font-mono">
                  Primary ({editorState.primaryImageMode}): Scale {editorState.zoomCoordinates.scale.toFixed(2)}, Pan ({editorState.zoomCoordinates.x}, {editorState.zoomCoordinates.y})
                  {editorState.comparisonMode !== 'single' && (
                    <div>Secondary ({editorState.secondaryImageMode}): Scale {editorState.secondaryZoomCoordinates.scale.toFixed(2)}, Pan ({editorState.secondaryZoomCoordinates.x}, {editorState.secondaryZoomCoordinates.y})</div>
                  )}
                </div>
              </div>
              
            </div>
          </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Save/Cancel Buttons - Sticky to Bottom */}
      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gray-50 border-t border-gray-200">
        <div className="flex flex-col gap-2">
          <Button 
            onClick={onSave}
            disabled={editorState.isUploading}
            className="w-full"
          >
            {editorState.isUploading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Crop'
            )}
          </Button>
          <Button 
            variant="outline" 
            onClick={onCancel}
            className="w-full"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};